import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Briefcase,
  ClipboardList,
  Activity,
  CalendarDays,
  Upload,
  Bell,
  Users,
  FileBarChart,
  History,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testId: "nav-overview" },
  { to: "/campaigns", label: "Campaigns", icon: Briefcase, testId: "nav-campaigns" },
  { to: "/planning", label: "Planning", icon: ClipboardList, testId: "nav-planning" },
  { to: "/execution", label: "Execution", icon: Activity, testId: "nav-execution" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar" },
  { to: "/upload", label: "Upload Center", icon: Upload, testId: "nav-upload" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testId: "nav-reports" },
  { to: "/notifications", label: "Notifications", icon: Bell, testId: "nav-notifications" },
  { to: "/admin", label: "Admin", icon: Users, testId: "nav-admin" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const pageTitle = NAV.find((n) => location.pathname.startsWith(n.to))?.label || "BWZ-OPS";

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex md:w-64 flex-col bg-zinc-950 text-zinc-300 sticky top-0 h-screen"
      >
        <div className="px-5 py-5 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-link">
            <BrandLogo to={null} size="sm" testId="sidebar-brand" />
            <div className="min-w-0">
              <div className="font-display font-bold text-white text-sm leading-tight">Brandon Wheelz</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-400 mt-0.5">OPS Central</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "bg-zinc-800 text-white border-l-2 border-[#0055FF] pl-[10px]"
                      : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100",
                  )
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-zinc-800">
          {user && (
            <div className="text-xs">
              <div className="text-zinc-400 truncate" data-testid="sidebar-user-email">{user.email}</div>
              <div className="text-zinc-500 capitalize mt-0.5" data-testid="sidebar-user-role">{user.role?.replace("_", " ")}</div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            data-testid="logout-button"
            className="mt-3 w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900"
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-zinc-200">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 min-w-0">
              <div className="md:hidden">
                <Link to="/dashboard" className="flex items-center gap-2">
                  <BrandLogo to={null} size="xs" testId="topbar-brand" />
                  <span className="font-display font-bold text-sm">BWZ</span>
                </Link>
              </div>
              <h1 className="font-display text-lg sm:text-xl font-semibold tracking-tight truncate" data-testid="page-title">
                {pageTitle}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {user && (
                <div className="hidden sm:flex items-center gap-2 text-xs text-zinc-600">
                  <div className="h-7 w-7 rounded-full bg-zinc-200 flex items-center justify-center font-semibold text-zinc-700">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span data-testid="topbar-user-name" className="font-medium">{user.name}</span>
                </div>
              )}
            </div>
          </div>
          {/* Mobile nav strip */}
          <nav className="md:hidden border-t border-zinc-200 overflow-x-auto" data-testid="mobile-nav">
            <div className="flex gap-1 px-3 py-2">
              {NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`mobile-${item.testId}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap",
                        isActive ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100",
                      )
                    }
                  >
                    <Icon className="h-3.5 w-3.5" /> {item.label}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
